import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import * as api from '../api';
import { toast } from 'react-toastify';
import { clearAuthStorage, getStoredToken, isTokenExpired } from '../../utils/tokenUtils';
import { applyLanguageFromCountry } from '../../utils/languageFromCountry';
import { getJobsLandingPath } from '../../utils/routingUtils';

export const login = createAsyncThunk("/auth/login", async({formData, navigate, onSuccess}, { rejectWithValue })=>{
    try {
        const response = await api.signIn(formData);
        
        // Log the response data to check country code and slug
        console.log("Login response data:", {
            result: response.data.result,
            countryCode: response.data.result?.countryCode,
            slug: response.data.result?.slug,
            rawResponse: response.data
        });
        
        toast.success("Uspešno ste se prijavili");
        
        // Ensure country code is properly stored
        const countryCode = response.data.result?.countryCode?.toLowerCase() || 'ba';
        const userData = {
            ...response.data,
            result: {
                ...response.data.result,
                countryCode: countryCode
            }
        };
        
        // Store the modified data
        localStorage.setItem("profile", JSON.stringify(userData));
        
        // Set userType to 'company' for admin/company login
        localStorage.setItem("userType", "company");
        // Clear worker-specific data if it exists
        localStorage.removeItem("workerPermissions");
        
        if (response.data.result && response.data.result.businessType) {
            console.log("Setting business type in session:", response.data.result.businessType);
            sessionStorage.setItem('businessType', response.data.result.businessType);
        } else if (response.data.businessType) {
            console.log("Setting business type in session from response root:", response.data.businessType);
            sessionStorage.setItem('businessType', response.data.businessType);
        }
        
        // Jezik aplikacije: Srbija → srpski, ostalo → engleski
        applyLanguageFromCountry(countryCode);

        // Check if user is super admin and redirect accordingly
        const isSuperAdmin = userData.result?.role === 'superadmin';
        
        // Get company slug for multi-tenant routing
        // Try multiple sources: userData, response, or fetch from localStorage if we have companyId
        let companySlug = userData.result?.slug || response.data.result?.slug;
        
        // If slug is missing, try to get it from localStorage (from previous session)
        if (!companySlug) {
            companySlug = localStorage.getItem('currentCompanySlug');
            console.log('⚠️ Slug not in response, trying localStorage:', companySlug);
        }
        
        console.log('🔍 Company slug check:', {
            fromUserData: userData.result?.slug,
            fromResponse: response.data.result?.slug,
            fromLocalStorage: localStorage.getItem('currentCompanySlug'),
            finalSlug: companySlug,
            companyId: userData.result?._id || response.data.result?._id
        });
        
        // Store slug in localStorage for later use
        if (companySlug) {
            localStorage.setItem('currentCompanySlug', companySlug);
            console.log('✅ Company slug stored:', companySlug);
        } else {
            console.warn('⚠️ No company slug found anywhere!');
            console.warn('⚠️ This means the company was created before slug feature was added.');
            console.warn('⚠️ Response data:', JSON.stringify(response.data, null, 2));
            console.warn('⚠️ Will use legacy /dashboard route');
        }
        
        if (onSuccess && typeof onSuccess === 'function') {
            onSuccess(userData);
        }
        
        // Navigate to appropriate dashboard based on role
        // Use setTimeout to ensure navigation happens after state is set
        setTimeout(() => {
            if (isSuperAdmin) {
                console.log('🔄 Redirecting superadmin to /dashboard/superadmin');
                localStorage.removeItem('currentCompanySlug');
                localStorage.removeItem('currentCompanyId');
                localStorage.removeItem('currentCompanyName');
                navigate("/dashboard/superadmin", { replace: true });
            } else if (companySlug) {
                const redirectPath = getJobsLandingPath(companySlug);
                console.log(`🔄 Redirecting to: ${redirectPath}`);
                navigate(redirectPath, { replace: true });
            } else {
                console.warn('⚠️ No company slug found, using legacy /dashboard/jobs route');
                navigate(getJobsLandingPath(), { replace: true });
            }
        }, 100);
        
        return userData;
    } catch (error) {
        const errorMessage = error.customMessage || error.response?.data?.message || "Greška prilikom prijave";
        return rejectWithValue({ message: errorMessage });
    }
});

export const registerCompany = createAsyncThunk("/auth/registerCompany", async({formData, navigate, toast: toastOverride, onSuccess}, { rejectWithValue })=>{
    const notifier = toastOverride || toast;
    try {
        // Check for early bird code in localStorage
        const earlyBirdCode = localStorage.getItem('earlyBirdCode');
        if (earlyBirdCode) {
            formData.earlyBirdCode = earlyBirdCode;
            console.log('✅ Early bird code found:', earlyBirdCode);
        }
        
        console.log("Sending registration request with data:", formData);
        const response = await api.signUpCompany(formData);
        
        // Sačuvaj business type odmah pri registraciji
        if (formData.businessType) {
            sessionStorage.setItem('businessType', formData.businessType);
        }

        // Check if email verification is required (can be 200 or 201 status)
        if (response.data?.requiresVerification || response.status === 200) {
            // Show message about email verification
            const message = response.data.message || "Registracija uspešna. Molimo proverite email za verifikaciju vaše email adrese.";
            notifier?.success?.(message);
            
            // Return data with email and companyName for modal
            return {
                ...response.data,
                requiresVerification: true,
                email: response.data?.result?.email || formData.email,
                companyName: response.data?.result?.companyName || formData.companyName
            };
        } else {
            // Show success message with early bird info if applied
            if (response.data?.earlyBirdApplied) {
                notifier?.success?.(response.data.message || "Uspešna registracija sa early bird popustom!");
            } else {
                notifier?.success?.("Uspešno ste se registrovali");
            }
            
            // Only navigate to login if email is already verified
            navigate("/auth?role=company&type=login");
        }
        
        // Clear early bird code after successful registration
        if (earlyBirdCode) {
            localStorage.removeItem('earlyBirdCode');
        }
        
        // Store company slug if available
        const companySlug = response.data?.result?.slug;
        if (companySlug) {
            localStorage.setItem('currentCompanySlug', companySlug);
            console.log('✅ Company slug stored after registration:', companySlug);
        }
        
        if (typeof onSuccess === 'function') {
            onSuccess(response.data);
        }
        
        return response.data;
    } catch (error) {
        console.error("Registration error details:", {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            message: error.message
        });
        
        // Check if this is actually a successful response that was caught as error
        if (error.response?.status === 201 || (error.response?.data && !error.response.data.message?.includes('went wrong'))) {
            // This might be a successful response that was incorrectly caught
            // Try to extract the data from response
            if (error.response?.data) {
                const responseData = error.response.data;
                
                // Check if email verification is required
                if (responseData?.requiresVerification) {
                    const message = responseData.message || "Registracija uspešna. Molimo proverite email za verifikaciju vaše email adrese.";
                    notifier?.success?.(message);
                    return responseData;
                }
            }
        }
        
        const errorMessage = error.response?.data?.message || error.message || "Greška prilikom registracije. Molimo pokušajte ponovo.";
        notifier?.error?.(errorMessage);
        return rejectWithValue(error.response?.data || { message: errorMessage });
    }
});

export const getUser = createAsyncThunk(
    "auth/getUser",
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.getUser();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const getCompany = createAsyncThunk(
    "auth/getCompany",
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.getCompany();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const changeBillingPlan = createAsyncThunk(
    "auth/changeBillingPlan",
    async (packageNum, { rejectWithValue }) => {
        try {
            await api.patchBillingPlan(packageNum);
            const response = await api.getCompany();
            return response.data;
        } catch (error) {
            const d = error.response?.data;
            return rejectWithValue(
                typeof d?.message === 'string' ? d.message : d || error.message || 'Greška'
            );
        }
    }
);

// Dodajemo funkciju za proveru validnosti tokena
export const checkTokenExpiry = () => {
    const token = getStoredToken();
    if (!token) return false;

    if (isTokenExpired(token)) {
        clearAuthStorage();
        return false;
    }

    return true;
};

export const logout = () => (dispatch) => {
  return new Promise((resolve) => {
    // Create spinner with countdown message
    let countdown = 3;
    document.getElementById('logout-message')?.remove();
    const messageDiv = document.createElement('div');
    messageDiv.id = 'logout-message';
    messageDiv.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 30px;
      border-radius: 8px;
      z-index: 9999;
      text-align: center;
      font-family: sans-serif;
      min-width: 300px;
    `;

    // Create spinner element
    const spinner = document.createElement('div');
    spinner.style.cssText = `
      width: 40px;
      height: 40px;
      margin: 0 auto 20px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid #3498db;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    `;

    // Add keyframes for spinner animation
    const styleSheet = document.createElement('style');
    styleSheet.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(styleSheet);

    messageDiv.appendChild(spinner);
    const textDiv = document.createElement('div');
    textDiv.textContent = `Logging out... Redirecting in ${countdown} seconds`;
    messageDiv.appendChild(textDiv);
    document.body.appendChild(messageDiv);

    const countdownInterval = setInterval(() => {
      countdown--;
      if (countdown > 0) {
        textDiv.textContent = `Logging out... Redirecting in ${countdown} seconds`;
      } else {
        clearInterval(countdownInterval);
        // Clear localStorage and sessionStorage first (zadrži kvarx lokacijske filtere)
        const preservedKvarxFilters = {};
        for (let i = 0; i < localStorage.length; i += 1) {
          const key = localStorage.key(i);
          if (key && key.startsWith('kvarx_location_filters_')) {
            preservedKvarxFilters[key] = localStorage.getItem(key);
          }
        }
        localStorage.clear();
        Object.entries(preservedKvarxFilters).forEach(([key, value]) => {
          if (value != null) localStorage.setItem(key, value);
        });
        sessionStorage.clear();
        
        // Then dispatch the logout action
        dispatch({ type: 'auth/setLogout' });
        
        // Remove the message div and redirect
        document.getElementById('logout-message')?.remove();
        
        // Check if user is worker or company to redirect to correct login page
        const userType = localStorage.getItem('userType') || 'company';
        const role = userType === 'worker' ? 'worker' : 'company';
        
        // For Netlify SPA, use relative path - Netlify will handle the redirect
        // This works better on mobile devices
        const redirectPath = `/auth?role=${role}&type=login`;
        
        // Use window.location.replace for better mobile support (doesn't add to history)
        // Netlify's redirect rule will catch this and serve index.html
        try {
          window.location.replace(redirectPath);
        } catch (error) {
          // Fallback to href if replace fails
          console.warn('window.location.replace failed, using href:', error);
          window.location.href = redirectPath;
        }
        
        resolve();
      }
    }, 1000);
  });
};

const authSlice = createSlice({
    name: 'auth',
    initialState:{
        user: null,
        error: '',
        loading: false,
        businessType: null,
        userType: localStorage.getItem('userType') || 'company'
    },
    reducers: {
        setUser: (state, action) => {
            state.user = action.payload;
            // Postavi business type kada se korisnik postavlja
            if (action.payload && action.payload.businessType) {
                state.businessType = action.payload.businessType;
            } else if (action.payload && action.payload.result && action.payload.result.businessType) {
                state.businessType = action.payload.result.businessType;
            }
            
            // Postavi userType iz localStorage
            const userType = localStorage.getItem('userType') || 'company';
            state.userType = userType;
            
            // Postavi ili produži trajanje sesije
            localStorage.setItem("lastActive", new Date().toISOString());
        },
        setLogout: (state, action) => {
            state.user = null;
            state.businessType = null;
            state.userType = 'company';
            
            // Očisti sva skladišta
            localStorage.removeItem('profile');
            localStorage.removeItem('token');
            localStorage.removeItem('lastActive');
            localStorage.removeItem('userType');
            localStorage.removeItem('workerPermissions');
            
            sessionStorage.removeItem('businessType');
            sessionStorage.clear();
        },
        setBusinessType: (state, action) => {
            state.businessType = action.payload;
            if (action.payload) {
                sessionStorage.setItem('businessType', action.payload);
                
                // Produži trajanje sesije
                localStorage.setItem("lastActive", new Date().toISOString());
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(login.pending, (state) => {
                state.loading = true;
                state.error = '';
            })
            .addCase(login.fulfilled, (state, action) => {
                state.loading = false;
                state.error = '';
                console.log("Login fulfilled with data:", {
                    user: action.payload,
                    countryCode: action.payload.result?.countryCode
                });
                
                // Ensure we store the data with proper country code
                const userData = {
                    ...action.payload,
                    result: {
                        ...action.payload.result,
                        countryCode: action.payload.result?.countryCode?.toLowerCase() || 'ba'
                    }
                };
                
                localStorage.setItem("profile", JSON.stringify(userData));
                state.user = userData;
                
                // Check if this is a worker login (worker login already sets userType in localStorage)
                const existingUserType = localStorage.getItem('userType');
                const isWorkerLogin = existingUserType === 'worker' || 
                                     action.payload?.userType === 'worker' ||
                                     (userData.result?.firstName && userData.result?.lastName && !userData.result?.companyName);
                
                console.log('🔐 Auth Reducer: Processing login fulfilled');
                console.log('  Existing userType:', existingUserType);
                console.log('  Payload userType:', action.payload?.userType);
                console.log('  Is worker login:', isWorkerLogin);
                console.log('  Profile structure - firstName:', userData.result?.firstName, 'companyName:', userData.result?.companyName);
                
                if (isWorkerLogin) {
                    // Worker login - keep worker type and permissions
                    console.log('✅ Confirming worker login - preserving userType and permissions');
                    localStorage.setItem("userType", "worker");
                    state.userType = 'worker';
                    // Don't remove workerPermissions - they were set by worker login
                } else {
                    // Company/admin login - set company type and clear worker data
                    console.log('✅ Confirming company/admin login - setting userType to company');
                    localStorage.setItem("userType", "company");
                    localStorage.removeItem("workerPermissions");
                    state.userType = 'company';
                }
                
                if (userData.result && userData.result.businessType) {
                    state.businessType = userData.result.businessType;
                } else if (userData.businessType) {
                    state.businessType = userData.businessType;
                }
            })
            .addCase(login.rejected, (state, action) => {
                state.loading = false;
                const errorMessage = action.payload ? action.payload.message : "Greška prilikom prijave";
                state.error = errorMessage;
                
                // Check if email verification is required
                if (action.payload?.requiresVerification) {
                    toast.error(errorMessage, {
                        autoClose: 5000,
                    });
                    // Optionally navigate to a page with resend verification option
                } else {
                    toast.error(errorMessage);
                }
            })
            .addCase(registerCompany.pending, (state) => {
                state.loading = true;
            })
            .addCase(registerCompany.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(registerCompany.rejected, (state, action) => {
                state.loading = false;
                console.error("Registration rejected:", action.payload);
                state.error = action.payload
                    ? action.payload.message
                    : "Error during registration";
            })
            .addCase(getUser.pending, (state, action) => {
                state.loading = true;
            })
            .addCase(getUser.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action?.payload;
            })
            .addCase(getUser.rejected, (state, action) => {
                state.loading = false;
            })
            .addCase(getCompany.pending, (state, action) => {
                state.loading = true;
            })
            .addCase(getCompany.fulfilled, (state, action) => {
                state.loading = false;
                // Update user with company data including packageUsage
                if (state.user && state.user.result) {
                    state.user.result = {
                        ...state.user.result,
                        ...action.payload,
                        packageUsage: action.payload.packageUsage || state.user.result.packageUsage
                    };
                } else {
                    // If user structure is different, create proper structure
                    state.user = {
                        result: action.payload,
                        token: state.user?.token
                    };
                }
                localStorage.setItem("profile", JSON.stringify(state.user));
            })
            .addCase(getCompany.rejected, (state, action) => {
                state.loading = false;
            })
            .addCase(changeBillingPlan.pending, (state) => {
                state.loading = true;
            })
            .addCase(changeBillingPlan.fulfilled, (state, action) => {
                state.loading = false;
                const payload = action.payload;
                if (state.user && state.user.result && payload) {
                    state.user.result = {
                        ...state.user.result,
                        ...payload,
                        packageUsage: payload.packageUsage || state.user.result.packageUsage,
                    };
                    localStorage.setItem("profile", JSON.stringify(state.user));
                }
            })
            .addCase(changeBillingPlan.rejected, (state) => {
                state.loading = false;
            });
    }
});

export const {setUser, setLogout, setBusinessType} = authSlice.actions;
export default authSlice.reducer;